import { QUESTION_TYPE } from '@components/data-driven-form/ddf.validation';
import { useJpa } from '@stores';
import { JpaTableInputCellData, JpaTableInputRowData } from '@components/table-input/tableInput.types';
import {
  FormRequestData,
  InternalAnswerSet,
  JpaAnswer,
  JpaContactAnswer,
  JpaQuestionData,
  JpaRequestAnswer,
  JpaTableAnswer,
  JpaValueType,
} from './types';
import { RequestFormData } from '@api/services/request/types';
import { filterNullEmptyObjects } from '@src/utils/helpers';

const excludeKeys = ['jpaRequestAnswerSetID', 'jpaQuestionID', 'jpaAnswerID', 'allowsMultiselect'];

const questionTypeMap: { [key: string]: number } = {
  jpaShortAnswerText: 1,
  jpaFullAnswerText: 2,
  jpaAnswerDate: 3,
  jpaAnswerNumber: 4,
  jpaAnswerYesNo: 5,
  hasJPAFileUploaded: 6,
  jpaListValues: 7,
};

const typePropertyMap = {
  [QUESTION_TYPE.shortAnswer]: 'jpaDynShortAnswerText',
  [QUESTION_TYPE.number]: 'jpaDynAnswerNumber',
  [QUESTION_TYPE.date]: 'jpaDynAnswerDate',
  [QUESTION_TYPE.yesNo]: 'jpaDynAnswerYesNo',
  [QUESTION_TYPE.option]: 'jpaDynListValues',
};

const defaultsJpaQuestionData = {
  typeId: 0,
  typeName: '',
  val: '',
};

const getMultiSelectValue = (val: string) =>
  val !== null
    ? String(val)
        ?.split(';')
        ?.map((item: string) => ({ value: item, label: item }))
    : undefined;

const getDateValue = (val: string) => String(val).split('T')[0];

export const formatContactAnswers = (jpaRequestAnswerSets: JpaRequestAnswer[]): JpaContactAnswer[] => {
  const seen = new Set();
  return jpaRequestAnswerSets.flatMap((jpaRequestAnswer: JpaRequestAnswer) =>
    (jpaRequestAnswer.jpaContactAnswers || []).filter((contactAnswer: JpaContactAnswer) => {
      const key = `${contactAnswer.firstName}-${contactAnswer.lastName}-${contactAnswer.emailAddress}-${contactAnswer.phoneOne}`;

      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    })
  );
};

// STORE FORMAT --> API FORMAT (updatePermit)
// convert internal answer obj to API answer format sent in the update request
// returns the "jpaRequestAnswerSets" payload object used in the updatePermit request
export const formatJpaRequestAnswerSet = (formData: RequestFormData) => {
  const answerSet = useJpa.getState().answerSet;
  const deletedTableAnswerIds: { [key: string]: number[] } = {};

  const answerSets: JpaRequestAnswer[] = Object.keys(answerSet).map((formIdKey: string) => {
    const formId = Number(formIdKey);
    const answerSetId = useJpa.getState().formRequestData?.[formId]?.answerSetId || 0;
    const formAnswers = answerSet[formId];
    const tableAnswers = useJpa.getState().tableAnswerSet[`f${formIdKey}`];
    const contactAnswers = useJpa.getState().jpaContactAnswerSet;

    const apiAnswerArr: JpaAnswer[] = Object.keys(formAnswers).map((questionIdKey) => {
      const answerData = { ...formAnswers[Number(questionIdKey)] };

      answerData.jpaQuestionID = Number(questionIdKey);
      delete answerData.jpaFormID;
      delete answerData.jpaQuestionTypeID;

      const filteredAnswerData = filterNullEmptyObjects(answerData);

      return filteredAnswerData;
    });

    const apiTableAnswerArr = Object.keys(tableAnswers || {}).reduce((acc: JpaTableAnswer[], questionIdKey: string) => {
      const tableData = tableAnswers[questionIdKey];
      const questionId = Number(questionIdKey.replace('q', ''));

      const tableValues = Object.keys(tableData).reduce((pageAcc: JpaTableAnswer[], pageKey: string) => {
        const pageData = tableData[pageKey].reduce((rowAcc: JpaTableAnswer[], row: JpaTableInputRowData) => {
          const rowValues = row.reduce((colAcc: JpaTableAnswer[], cell: JpaTableInputCellData, cellIndex) => {
            if ((cell?.value === undefined || cell.value) === null) {
              return colAcc;
            } else if (cell?.isDeleted) {
              deletedTableAnswerIds[formId] = deletedTableAnswerIds[formId] || [];
              deletedTableAnswerIds[formId].push(cell.answerId);

              return colAcc;
            } else {
              const value =
                cell.typeId === QUESTION_TYPE.yesNo && cell.value !== null ? (cell.value ? 1 : 0) : cell.value;
              const valueKey = typePropertyMap[cell.typeId];

              return [
                ...colAcc,
                {
                  jpaQuestionID: questionId,
                  jpaDynColumnNumber: cellIndex + 1,
                  jpaDynRowNumber: cell.rowNumber as number,
                  jpaDynTableAnswerID: cell.answerId,
                  jpaRequestAnswerSetID: answerSetId,
                  [valueKey]: value,
                },
              ];
            }
          }, []);

          return [...rowAcc, ...rowValues];
        }, []);

        return [...pageAcc, ...pageData];
      }, []);

      return [...acc, ...tableValues];
    }, []);

    const formDeletedTableAnswerIds = deletedTableAnswerIds[formId] || [];

    const apiContactAnswerArr = Object.values(contactAnswers).map((contactAnswer) => ({
      ...contactAnswer,
    }));

    return {
      answers: apiAnswerArr,
      jpaContactAnswers: apiContactAnswerArr,
      jpaDynamicTableAnswers: apiTableAnswerArr,
      jpaDynamicTableDeletedAnswers: formDeletedTableAnswerIds,
      jpaFormID: formId,
      jpaRequestAnswerSetID: answerSetId,
      requestID: formData.request.requestID,
      version: formData.request.version || 0,
    };
  });

  return {
    ...formData,
    request: {
      ...formData.request,
      jpaRequestAnswerSets: answerSets,
    },
  };
};

// API FORMAT (getPermit/updatePermit) --> STORE FORMAT
// convert API JPA info to internal store format
// returns:
//   formattedAnswerSet: contains an object for each formId that is passed directly to the API when saving
//   formRequestData: maps formId to jpaRequestAnswerSetID, requestID, and version
//   rhfDefaultValues: maps type-questionID to the input value -> use to render inputs in react-hook-form
export const convertApiAnswerData = (jpaRequestAnswerSets: JpaRequestAnswer[]) => {
  const formattedAnswerSet: InternalAnswerSet = {};
  const formRequestData: FormRequestData = {};
  const rhfDefaultValues: { [key: string]: JpaValueType | undefined } = {};

  jpaRequestAnswerSets?.forEach((answerSet: JpaRequestAnswer) => {
    formattedAnswerSet[answerSet.jpaFormID] = {};
    formRequestData[answerSet.jpaFormID] = {
      answerSetId: answerSet.jpaRequestAnswerSetID,
      requestId: answerSet.requestID,
      version: answerSet.version,
    };

    // non-table answers
    answerSet?.answers?.forEach((answer: JpaAnswer) => {
      const questionData: JpaQuestionData = { ...defaultsJpaQuestionData };

      Object.keys(answer).forEach((key: string) => {
        if (!excludeKeys.includes(key) && answer[key] !== null) {
          questionData.typeId = questionTypeMap[key];
          questionData.typeName = key;
          questionData.val = answer[key];
        }
      });

      formattedAnswerSet[answerSet.jpaFormID][answer.jpaQuestionID] = {
        jpaFormID: answerSet.jpaFormID,
        jpaQuestionID: answer.jpaQuestionID,
        jpaAnswerID: answer.jpaAnswerID,
        jpaQuestionTypeID: questionData.typeId,
        [questionData.typeName]: questionData.val,
      };

      Object.keys(answer).forEach((key: string) => {
        if (key === 'allowsMultiselect' && typeof answer[key] === 'boolean') {
          questionData.allowsMultiselect = answer[key];
        }
        if (!excludeKeys.includes(key) && answer[key] !== null) {
          questionData.typeId = questionTypeMap[key];
          questionData.typeName = key;
        }
      });

      let value: JpaValueType | undefined;

      if (questionData.typeId === QUESTION_TYPE.option && questionData.allowsMultiselect) {
        value = getMultiSelectValue(answer[questionData.typeName] as string);
      } else if (questionData.typeId === QUESTION_TYPE.date) {
        value = getDateValue(answer?.[questionData.typeName] as string);
      } else {
        value = answer[questionData.typeName];
      }

      rhfDefaultValues[`${questionData.typeId}-${answer.jpaQuestionID}`] = value;
    });
  });

  return {
    formattedAnswerSet,
    formRequestData,
    rhfDefaultValues,
  };
};
